import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, Clock, Mail, XCircle } from "lucide-react";

export interface InvitationError {
  code: string;
  message: string;
  details?: {
    invitedEmail?: string;
    authenticatedEmail?: string;
    expiresAt?: number;
    [key: string]: any;
  };
}

interface InvitationErrorDialogProps {
  open: boolean;
  error: InvitationError | null;
  onClose: () => void;
  onSwitchAccount?: () => void;
  onRequestNewInvite?: () => void;
}

export function InvitationErrorDialog({
  open,
  error,
  onClose,
  onSwitchAccount,
  onRequestNewInvite,
}: InvitationErrorDialogProps) {
  if (!error) return null;

  // EMAIL_MISMATCH: User logged in with wrong email
  if (error.code === "EMAIL_MISMATCH") {
    return (
      <AlertDialog open={open} onOpenChange={onClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2 text-destructive">
              <Mail className="h-5 w-5" />
              <AlertDialogTitle>Wrong Email Address</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-3">
              <p>
                This invitation is for a different email address than the one
                you're signed in with.
              </p>

              <div className="space-y-2">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    Invitation is for:
                  </p>
                  <div className="font-mono text-sm bg-muted p-2 rounded">
                    {error.details?.invitedEmail}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    You're signed in as:
                  </p>
                  <div className="font-mono text-sm bg-muted p-2 rounded">
                    {error.details?.authenticatedEmail}
                  </div>
                </div>
              </div>

              <p className="text-sm">
                To accept this invitation, you need to either:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>Log out and sign in with {error.details?.invitedEmail}</li>
                <li>Request a new invitation for your current email</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              onClick={onClose}
              variant="outline"
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            {onRequestNewInvite && (
              <Button
                onClick={onRequestNewInvite}
                variant="outline"
                className="w-full sm:w-auto"
              >
                Request New Invite
              </Button>
            )}
            {onSwitchAccount && (
              <Button onClick={onSwitchAccount} className="w-full sm:w-auto">
                Log Out and Switch Account
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // INVITATION_EXPIRED: Token older than 7 days
  if (error.code === "INVITATION_EXPIRED") {
    return (
      <AlertDialog open={open} onOpenChange={onClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2 text-amber-600">
              <Clock className="h-5 w-5" />
              <AlertDialogTitle>Invitation Expired</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-3">
              <p>This invitation has expired and can no longer be used.</p>

              <p className="text-sm">
                Invitations are valid for 7 days. Please contact the workspace
                administrator to send you a new invitation.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {onRequestNewInvite && (
              <Button onClick={onRequestNewInvite} variant="outline">
                Request New Invite
              </Button>
            )}
            <Button onClick={onClose}>Close</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // INVITATION_NOT_FOUND: Invalid/used token
  if (error.code === "INVITATION_NOT_FOUND") {
    return (
      <AlertDialog open={open} onOpenChange={onClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2 text-destructive">
              <XCircle className="h-5 w-5" />
              <AlertDialogTitle>Invitation Not Found</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-3">
              <p>This invitation is invalid or has already been used.</p>

              <p className="text-sm">Common reasons:</p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>The invitation has already been accepted</li>
                <li>The invitation was cancelled by an administrator</li>
                <li>The invitation link is incomplete or corrupted</li>
              </ul>

              <p className="text-sm">
                If you believe this is an error, please contact the workspace
                administrator.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {onRequestNewInvite && (
              <Button onClick={onRequestNewInvite} variant="outline">
                Request New Invite
              </Button>
            )}
            <Button onClick={onClose}>Close</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // Generic error fallback
  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <AlertDialogTitle>Error Processing Invitation</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-3">
            <p>
              {error.message ||
                "An unexpected error occurred while processing your invitation."}
            </p>

            {error.code && (
              <p className="text-xs text-muted-foreground font-mono">
                Error code: {error.code}
              </p>
            )}

            <p className="text-sm">
              Please try again or contact support if the problem persists.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button onClick={onClose}>Close</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
